import React, {
  Activity,
  CSSProperties,
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { VirtuosoHandle } from 'react-virtuoso';
import { debounce } from 'underscore';
import classNames from 'classnames';

import styles from './custom-select.module.css';

import { TReactSelectOption } from '../react-select/custom-react-select.types';
import CustomSelectUnfocusedItems from './components/custom-select-unfocused-items';
import CustomSelectMenu from './components/custom-select-menu';
import CustomSelectHeader from './components/custom-select-header';
import CustomSelectFocusedItems from './components/custom-select-focused-items';
import CustomSelectInput from './components/custom-select-input/custom-select-input';
import selectKeyDownCapture from './helpers/select-keydown-capture';
import useResetAttributes from './helpers/use-reset-attributes';
import useScrollResets from './helpers/use-scroll-resets';
import useFilteredOptions from './helpers/use-filtered-options';
import FakeBorder from './components/fake-border';
import clearHighlightedSelection from '@/utils/clear-highlighted-selection';
import { TCustomSelectProps } from './custom-select.types';
import Container from '@ui/container/container';
import useClickAwayListener from '@/utils/use-click-away-listener';
import { mergeRefs } from '@/utils/merge-refs';
import useGetInputPadding from './helpers/use-get-input-padding';
import useInputKeyUpListener from './helpers/use-input-keyup-listener';
import useInputBlurListener from './helpers/use-input-blur-listener';
import useVerticalInputRowFit from './helpers/use-vertical-input-row-fit';
import useVerticalLabelMetrics from './helpers/use-vertical-label-metrics';
import useSelectClipPath from './helpers/use-select-clip-path';

const CustomSelect = memo(
  ({
    id,
    Placeholder,
    options: allOptions,
    onFocusClosed,
    defaultOptions,
    controlledOptions,
    onFocusChange,
    RenderFocusedLabelContent,
    RenderUnfocusedLabelContent,
    RenderMenuOptionContent,
    RenderNoOptionsContent,
    lockActiveState: lockActiveStateProps = false,
    onAddNewSelect,
    labelHeight = '42px',
    RenderHeaderLabelContent,
    onMenuClose,
    onMenuOpen,
    hideHeader,
    onChange,
    filterSelected = true,
    autoFocus,
    inputRef: propsInputRef,
    containerRef: propsContainerRef,
    disabled,
    isMultiSelect = true,
    isSearchable = true,
    selectFirstSearchOptionOnEnter = true,
    invisibleTriggerRef,
    menuOptionSelectedIndicatorLabel,
    clearInputOnEnter = true,
    clearInputOnMouseSelect = true,
    dynamicMenuPlacement = false,
    permanentDropdownIcon,
    permanentDropdownIconUnlessHasSelectedOptions,
    hidePlaceholderOnInputFocus = false,
    hidePlaceholderIfInputHasValue = true,
    design = 'standard',
    virtualizeMenu = true,
    inputPadding,
    showEditTextOnHover,
    unfocusedItemsGap,
    status,
    metaImageKey,
    hideDropdownIcon,
    containerClassName = '',
    hideMenu,
    clipMenuOverflow = true,
    onEnterKeyPress,
    onKeyUp,
    onInputBlur,
    clearInputOnBlur = true,
    direction = 'horizontal',
    minRows = 1,
    maxRows = 4,
    unfocusedLeftOffset,
    disableAddNewIfNoInputValue,
    onSearchChange,
    onMenuEndReached,
    isLoadingOptions,
    disableClientFilter,
  }: TCustomSelectProps) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const selectRef = useRef<HTMLDivElement | null>(null);
    const selectedOptionsRef = useRef<HTMLDivElement | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const menuShellRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const labelRef = useRef<HTMLLabelElement | null>(null);
    const chipNodesRef = useRef<Array<HTMLDivElement | null>>([]);
    const virtuosoRef = useRef<VirtuosoHandle | null>(null);
    const virtuosoNavRef = useRef({ activeIndex: -1, lastNavTime: 0 });

    const hasTriggeredAutoFocus = useRef(false);

    const [searchValue, setSearchValue] = useState('');
    const [menuOpen, setMenuOpen] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState<TReactSelectOption[]>(
      controlledOptions || defaultOptions || []
    );

    useEffect(() => {
      if (autoFocus && !hasTriggeredAutoFocus.current) {
        hasTriggeredAutoFocus.current = true;
        inputRef?.current?.focus();
      }
    }, [autoFocus]);

    // Sync only when controlledOptions' values actually change — not on every
    // new array reference. Parents commonly pass an inline array, so this effect
    // would otherwise fire on every parent render and could stomp fresh internal
    // state during the 150ms onChange debounce window (flashing the prior value).
    const prevControlledOptionsRef = useRef(controlledOptions);
    useEffect(() => {
      if (!controlledOptions) return;
      const prev = prevControlledOptionsRef.current;
      const sameContent =
        !!prev &&
        prev.length === controlledOptions.length &&
        prev.every((p, i) => p.value === controlledOptions[i].value);
      prevControlledOptionsRef.current = controlledOptions;
      if (sameContent) return;
      setSelectedOptions(controlledOptions);
    }, [controlledOptions]);

    useEffect(() => {
      if (disabled) {
        setMenuOpen(false);
        setIsFocused(false);
        setSearchValue('');
      }
    }, [disabled]);

    // a hidden menu is a closed menu - otherwise the next click toggles the stale open state shut.
    // Re-checked whenever the open/focus state changes because the input can be re-focused while hidden
    // (e.g. a parent modal's focus lock restoring focus to it), and onFocus re-opens the menu
    useEffect(() => {
      if (!hideMenu) return;
      if (menuOpen) setMenuOpen(false);
      if (isFocused) setIsFocused(false);
    }, [hideMenu, menuOpen, isFocused]);

    // Use refs to store the latest callbacks to avoid infinite loops
    // when parent passes a new function reference on each render
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    const onFocusClosedRef = useRef(onFocusClosed);
    onFocusClosedRef.current = onFocusClosed;

    const onFocusChangeRef = useRef(onFocusChange);
    onFocusChangeRef.current = onFocusChange;

    const onMenuCloseRef = useRef(onMenuClose);
    onMenuCloseRef.current = onMenuClose;

    const onMenuOpenRef = useRef(onMenuOpen);
    onMenuOpenRef.current = onMenuOpen;

    useInputKeyUpListener({ inputRef, onKeyUp });
    useInputBlurListener({ inputRef, onBlur: onInputBlur });

    // Create a stable debounced function
    // otherwise adding/removing too fast (like holding down the enter/backspace keys) creates infinite loops
    const debouncedOnChange = useMemo(
      () =>
        debounce((options: TReactSelectOption[]) => {
          onChangeRef.current?.(options);
        }, 150),
      []
    );

    // Wraps setSelectedOptions so user-driven changes also notify the parent via onChange.
    // Parent-driven syncs (controlledOptions effect) intentionally call setSelectedOptions
    // directly so the parent doesn't get echoed its own value back.
    const handleSelectionChange = useCallback<Dispatch<SetStateAction<TReactSelectOption[]>>>(
      (updater) => {
        setSelectedOptions((prev) => {
          const next =
            typeof updater === 'function'
              ? (updater as (p: TReactSelectOption[]) => TReactSelectOption[])(prev)
              : updater;
          debouncedOnChange(next);
          return next;
        });
      },
      [debouncedOnChange]
    );

    const selectableOptions = useFilteredOptions({
      searchValue,
      selectedOptions,
      allOptions,
      filterSelected,
      disableClientFilter,
    });

    const onSearchChangeRef = useRef(onSearchChange);
    onSearchChangeRef.current = onSearchChange;
    useEffect(() => {
      onSearchChangeRef.current?.(searchValue);
    }, [searchValue]);

    // Create Sets for O(1) lookups
    const selectedValuesSet = useMemo(() => new Set(selectedOptions.map((opt) => opt.value)), [selectedOptions]);

    const allOptionsMap = useMemo(() => new Map(allOptions.map((opt) => [opt.value, opt])), [allOptions]);

    const hasActivatedAtleastOnceRef = useRef(false);
    useEffect(() => {
      if (isFocused || lockActiveStateProps || menuOpen) {
        hasActivatedAtleastOnceRef.current = true;
      }
    }, [isFocused, lockActiveStateProps, menuOpen]);

    const hasActivatedAtleastOnce = hasActivatedAtleastOnceRef.current;

    const lockActiveState = menuOpen || isFocused || lockActiveStateProps;

    useEffect(() => {
      if (menuOpen) onMenuOpenRef.current?.();
      if (!menuOpen && hasActivatedAtleastOnce) {
        onMenuCloseRef.current?.();
      }
    }, [hasActivatedAtleastOnce, menuOpen]);

    useEffect(() => {
      if (!lockActiveStateProps && !isFocused && hasActivatedAtleastOnce) {
        onFocusClosedRef.current?.(selectedOptions);
      }
    }, [lockActiveStateProps, isFocused, selectedOptions, hasActivatedAtleastOnce]);

    useEffect(() => {
      onFocusChangeRef.current?.(lockActiveState);
    }, [lockActiveState]);

    useScrollResets({
      selectRef,
      inputRef,
      setSearchValue,
      lockActiveState,
      clearInputOnBlur,
    });

    useResetAttributes({
      lockActiveStateProps,
      lockActiveState,
      menuOpen,
      menuRef,
      isFocused,
      selectedOptionsRef,
      setMenuOpen,
    });

    const isVertical = direction === 'vertical';

    const needsRowBreak = useVerticalInputRowFit({
      containerRef: labelRef,
      chipNodesRef,
      selectedOptionsLength: selectedOptions.length,
      direction,
      lockActiveState,
    });

    // In vertical mode, when a chip is added past maxRows, scroll label to bottom
    // so the input remains visible. (Scroll lives on .label, not .select, so the
    // header stays static.)
    useEffect(() => {
      if (!isVertical || !lockActiveState) return;
      const el = labelRef.current;
      if (!el) return;
      el.scrollTo({ top: el.scrollHeight });
    }, [isVertical, lockActiveState, selectedOptions.length]);

    const isMultiRow = useVerticalLabelMetrics({
      isVertical,
      labelRef,
      containerRef,
      deps: [selectedOptions.length, lockActiveState],
    });

    useSelectClipPath({
      selectRef,
      labelRef,
      isVertical,
      isActive: lockActiveState,
    });

    const getInputPadding = useGetInputPadding({
      design,
      inputPadding,
      selectedOptions,
      isFocused: lockActiveStateProps || !!lockActiveState || !!isFocused,
      displayingDropdownIcon:
        permanentDropdownIconUnlessHasSelectedOptions !== undefined
          ? selectedOptions.length
            ? false
            : true
          : permanentDropdownIcon,
    });

    const handleToggleMenu = () => {
      if (lockActiveStateProps || !lockActiveState) return;
      setMenuOpen((prev) => !prev);
    };

    const handleRemoveClick = (option: string | number) => (e: React.MouseEvent<HTMLSpanElement>) => {
      e.stopPropagation();
      e.preventDefault();
      handleSelectionChange((prev) => prev.filter((so) => so.value.toString() !== option.toString()));
    };

    const handlePreventDefaultClick = (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      e.preventDefault();
    };

    useClickAwayListener({
      ref: containerRef,
      open: true,
      onPointerDownInside: () => {
        if (lockActiveStateProps || disabled || lockActiveState) return;

        setIsFocused(true);
        setMenuOpen((prev) => !prev);
        inputRef?.current?.click();
        inputRef?.current?.focus();
      },
      onPointerDownOutside: (event) => {
        if (lockActiveStateProps || disabled) return;
        const target = event.target instanceof HTMLElement ? event.target : null;
        if (!target) return;
        // menu is in a portal, so we need to check manually. When the menu is
        // wrapped in a clip-overflow shell, the shell is the outermost element
        // and contains both .menu and any sibling footer — checking it covers
        // both shapes of the DOM tree.
        const outerRef = clipMenuOverflow ? menuShellRef : menuRef;
        if (outerRef.current?.contains(target) || outerRef.current === target) {
          return;
        }
        setIsFocused(false);
        setMenuOpen(false);
      },
    });

    return (
      <Container
        className={classNames('position-relative', styles['custom-select-container'], styles[design], {
          'overflow-hidden': lockActiveState && !isVertical,
          [styles['disabled']]: disabled,
          [styles['vertical']]: isVertical,
        })}
        background="inherit"
        inert={disabled}
        ref={mergeRefs(containerRef, propsContainerRef)}
        data-direction={direction}
        data-multi-row={isMultiRow ? 'true' : 'false'}
        style={
          {
            '--custom-select-control-height': labelHeight,
            '--custom-select-input-padding': getInputPadding(),
            '--custom-select-min-rows': minRows,
            '--custom-select-max-rows': Number.isFinite(maxRows) ? maxRows : 'none',
          } as CSSProperties
        }
      >
        <span
          ref={invisibleTriggerRef}
          style={{ opacity: 0, visibility: 'hidden', height: 0, width: 0, position: 'absolute' }}
          onClick={() => {
            setMenuOpen(true);
            setIsFocused(true);
          }}
        />
        <div
          data-component="CustomSelect"
          ref={selectRef}
          role="combobox"
          aria-expanded={menuOpen}
          aria-label="Select options"
          className={classNames(styles.select, styles[design], containerClassName, {
            [styles['is-focused']]: lockActiveState,
            [styles['has-value']]: !!selectedOptions?.length,
            [styles['disabled']]: disabled,
            [styles['vertical']]: isVertical,
          })}
          data-direction={direction}
          data-select-focused={lockActiveState}
          onKeyDownCapture={selectKeyDownCapture({
            lockActiveStateProps,
            lockActiveState,
            menuOpen,
            setMenuOpen,
            selectedOptionsRef,
            menuRef,
            selectRef,
            inputRef,
            selectedOptions,
            selectedValuesSet,
            setSelectedOptions: handleSelectionChange,
            onAddNewSelect,
            selectableOptions,
            allOptionsMap,
            selectFirstSearchOptionOnEnter,
            clearInputOnEnter,
            setSearchValue,
            virtuosoRef: virtualizeMenu ? virtuosoRef : undefined,
            virtuosoNavRef: virtualizeMenu ? virtuosoNavRef : undefined,
            isMultiSelect,
            onEnterKeyPress,
          })}
          onPointerDownCapture={() => {
            // because we're prevent defaults in a lot of places, when the page has highlighted text
            // this entire select feature requires two clicks to get into it: one to remove highlight text, then one to enter
            // so we first need to clear all highlighted text to avoid that issue.
            // but only if we're not already "active", or the input itself won't be selectable
            if (!lockActiveState) {
              clearHighlightedSelection();
            }
          }}
        >
          <Activity mode={hideHeader ? 'hidden' : 'visible'}>
            <CustomSelectHeader
              handlePreventDefaultClick={handlePreventDefaultClick}
              RenderHeaderLabelContent={RenderHeaderLabelContent}
              isFocused={isFocused}
              inputRef={inputRef}
              handleToggleMenu={handleToggleMenu}
              selectedOptionsCount={selectedOptions.length}
              showEditTextOnHover={!!showEditTextOnHover}
            />
          </Activity>

          <label
            htmlFor={id}
            role="presentation"
            ref={labelRef}
            data-row-break={isVertical && lockActiveState && needsRowBreak ? 'true' : 'false'}
            className={classNames(styles.label, styles[design] || '', {
              [styles['lock-active-state']]: lockActiveState,
              [styles['vertical']]: isVertical,
            })}
            onClick={handleToggleMenu} /* this lets child element clicks */
            onPointerDown={handlePreventDefaultClick}
            onPointerUp={(e) => {
              if (e.target instanceof HTMLLabelElement) {
                handleToggleMenu(); /* this enures we can can still "click" this component itself */
              }
            }}
          >
            <CustomSelectFocusedItems
              handlePreventDefaultClick={handlePreventDefaultClick}
              handleToggleMenu={handleToggleMenu}
              selectedOptions={selectedOptions}
              selectedOptionsRef={selectedOptionsRef}
              RenderFocusedLabelContent={RenderFocusedLabelContent}
              lockActiveState={lockActiveState}
              handleRemoveClick={handleRemoveClick}
              direction={direction}
              chipNodesRef={chipNodesRef}
            />

            <CustomSelectUnfocusedItems
              RenderUnfocusedLabelContent={RenderUnfocusedLabelContent}
              selectedOptions={selectedOptions}
              selectRef={selectRef}
              lockActiveState={lockActiveState}
              isMultiSelect={isMultiSelect}
              design={design}
              metaImageKey={metaImageKey}
              displayingDropdownIcon={
                permanentDropdownIconUnlessHasSelectedOptions !== undefined
                  ? selectedOptions.length
                    ? false
                    : true
                  : permanentDropdownIcon
              }
              gap={unfocusedItemsGap}
              direction={direction}
              maxRows={maxRows}
              leftOffset={unfocusedLeftOffset}
            />

            <CustomSelectInput
              inputRef={inputRef}
              menuRef={menuRef}
              propsInputRef={propsInputRef}
              selectedOptionsRef={selectedOptionsRef}
              id={id}
              lockActiveState={lockActiveState}
              hasSelectedOptions={!!selectedOptions.length}
              Placeholder={Placeholder}
              setIsFocused={setIsFocused}
              setMenuOpen={setMenuOpen}
              setSearchValue={setSearchValue}
              disabled={disabled}
              hidePlaceholderOnInputFocus={hidePlaceholderOnInputFocus}
              hidePlaceholderIfInputHasValue={hidePlaceholderIfInputHasValue}
              isSearchable={isSearchable}
            />

            <Activity mode={hideMenu ? 'hidden' : 'visible'}>
              <CustomSelectMenu
                menuOpen={menuOpen}
                menuRef={menuRef}
                menuShellRef={menuShellRef}
                clipMenuOverflow={clipMenuOverflow}
                selectRef={selectRef}
                dynamicMenuPlacement={dynamicMenuPlacement}
                handlePreventDefaultClick={handlePreventDefaultClick}
                selectableOptions={selectableOptions}
                setSelectedOptions={handleSelectionChange}
                onAddNewSelect={onAddNewSelect}
                inputRef={inputRef}
                RenderMenuOptionContent={RenderMenuOptionContent}
                RenderNoOptionsContent={RenderNoOptionsContent}
                selectedValuesSet={selectedValuesSet}
                menuOptionSelectedIndicatorLabel={menuOptionSelectedIndicatorLabel}
                virtualizeMenu={virtualizeMenu}
                virtuosoRef={virtuosoRef}
                isMultiSelect={isMultiSelect}
                metaImageKey={metaImageKey}
                disableAddNewIfNoInputValue={disableAddNewIfNoInputValue}
                onMenuEndReached={onMenuEndReached}
                isLoadingOptions={isLoadingOptions}
                onSelectOption={({ status }) => {
                  if (status === 'add-new') {
                    if (clearInputOnMouseSelect) {
                      setSearchValue('');
                      if (inputRef?.current) {
                        inputRef.current.value = '';
                      }
                    }
                    return;
                  }
                  if (clearInputOnMouseSelect && status === 'selected') {
                    setSearchValue('');
                    if (inputRef?.current) {
                      inputRef.current.value = '';
                    }
                    // scroll to top of menu
                    menuRef?.current?.scrollTo({ top: 0 });
                  }
                  if (!isMultiSelect) {
                    setMenuOpen(false);
                    inputRef?.current?.blur();
                  }
                }}
              />
            </Activity>
          </label>
        </div>

        <FakeBorder
          lockActiveState={lockActiveState}
          menuOpen={menuOpen}
          permanentDropdownIcon={
            permanentDropdownIconUnlessHasSelectedOptions !== undefined
              ? selectedOptions.length
                ? false
                : true
              : permanentDropdownIcon
          }
          design={design}
          disabled={disabled}
          status={status}
          hideDropdownIcon={hideDropdownIcon}
        />
      </Container>
    );
  }
);

CustomSelect.displayName = 'CustomSelect';

export default CustomSelect;
